import * as VisuallyHidden from "@radix-ui/react-visually-hidden"
import { observer } from "mobx-react-lite"
import { useAccount, useConfig } from "wagmi"

import { Button } from "@/components/button"
import { Card } from "@/components/card"
import { DialogTitle } from "@/components/dialog"
import CloseDialogX from "@/components/icons/close-dialog-x"
import { ScrollArea } from "@/components/scroll-area"
import { Separator } from "@/components/separator"
import { Sheet, SheetClose, SheetContent } from "@/components/sheet"
import { useScaleApplication } from "@/domains/application/use-scale-application"
import { approveSuperVoterForAllInteraction } from "@/domains/interactions"
import { useChainConfig } from "@/hooks/use-chain-config"
import useEventEmitter from "@/hooks/useEventEmitter"

import { SuperVoteLogo } from "../super-vote-logo"
import { superVoterFeatures } from "./super-voter-features"

// eslint-disable-next-line @typescript-eslint/no-empty-function
const noop = () => {}

const SuperVoterAccessDialog = observer(
  ({ isOpen, setIsOpen }: { isOpen: boolean; setIsOpen: (isOpen: boolean) => void }) => {
    const account = useAccount()
    const emitter = useEventEmitter()
    const config = useConfig()
    const chainConfig = useChainConfig()
    const scaleApp = useScaleApplication()

    if (!scaleApp) {
      return null
    }

    const hasSuperVoterAccess = scaleApp.Pages.Vote.Store.hasSuperVoterAccess

    const onGrantAccessClick = async () => {
      try {
        if (!account.address) {
          return false
        }
        const result = await approveSuperVoterForAllInteraction({
          account: account.address,
          chainId: chainConfig.chainId,
          config,
          vestedAddress: chainConfig.tokens.vested.address,
          superVoterAddress: chainConfig.equalizer.contracts.superVoter,
          emitter,
          eventBus: scaleApp.EventBus,
        })

        if (result.success) {
          setIsOpen(false)
        }
        console.log("GrantAccessDialog→onGrantAccessClick→result", result)
      } catch (error) {
        console.error("GrantAccessDialog→onGrantAccessClick→error", error)
      }
    }

    if (hasSuperVoterAccess === false) {
      return (
        <Sheet modal={true} open={isOpen} onOpenChange={setIsOpen}>
          <SheetContent side="nft_dialog" className="flex h-full flex-col p-0">
            <div className="flex flex-col gap-1 px-2 py-4 sm:p-4">
              <div className="flex items-center justify-between">
                <p className="text-xl font-medium text-t100">Grant Access to SuperVoter™ </p>
                <VisuallyHidden.Root>
                  <DialogTitle>Grant access to super voter</DialogTitle>
                </VisuallyHidden.Root>
                <SheetClose>
                  <CloseDialogX onClick={noop} />
                </SheetClose>
              </div>
              <div className="mb-4 text-sm text-t200">Access is required to enable advanced voting features.</div>
              <Separator
                className="relative h-0 w-full overflow-visible rounded-md border border-solid border-p400-light
          after:pointer-events-none after:absolute after:inset-[-1px] after:z-[1] after:rounded-md 
          after:shadow-[inset_0.5px_0.5px_0.5px_0px_hsla(220,39%,19%,1)] after:content-['']"
                decorative
              />
            </div>
            <div className="flex flex-grow flex-col gap-4 overflow-hidden px-4">
              <p className="text-t200">Features:</p>
              <ScrollArea>
                <Card.Container className="border-p400-light bg-p400">
                  {superVoterFeatures.map((feature) => (
                    <div key={feature.title}>
                      <p className="font-medium text-t100">{feature.title}</p>
                      <p className="text-t200">{feature.description}</p>
                    </div>
                  ))}
                </Card.Container>
              </ScrollArea>
            </div>
            <Separator
              className="relative h-0 w-full overflow-visible rounded-md border border-solid
          border-p400-light after:pointer-events-none after:absolute after:inset-[-1px] after:z-[1] 
          after:rounded-md after:shadow-[inset_0.5px_0.5px_0.5px_0px_hsla(220,43%,5%,1)] after:content-['']"
              decorative
            />
            <div className="flex justify-end px-4 pb-4">
              <Button
                variant="primary"
                onClick={async () => {
                  await onGrantAccessClick()
                  setIsOpen(false)
                }}
              >
                Grant Access
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      )
    }

    return (
      <Sheet modal={true} open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent side="nft_dialog" className="flex h-full flex-col p-0">
          <div className="flex flex-col gap-1 px-2 py-4 sm:p-4">
            <div className="flex items-center justify-between">
              <SuperVoteLogo />
              <SheetClose>
                <CloseDialogX onClick={noop} />
              </SheetClose>
            </div>
            <div className="mb-6 text-sm text-t200">
              SuperVoter access is already granted for this address, nothing to do, please close this dialog.
            </div>
            <Separator
              className="relative h-0 w-full overflow-visible rounded-md border border-solid
          border-p400-light after:pointer-events-none after:absolute after:inset-[-1px] after:z-[1] 
          after:rounded-md after:shadow-[inset_0.5px_0.5px_0.5px_0px_hsla(220,43%,5%,1)] after:content-['']"
              decorative
            />
            <Button variant="neutral" onClick={() => setIsOpen(false)}>
              Close
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    )
  },
)

export default SuperVoterAccessDialog
